/**
 * Sessão no navegador (site e jogo, cada um na sua origem): o JWT da API, o id da conta e o apelido
 * (ADR 0005: `Authorization: Bearer`, sem cookies). Ninguém aqui confere a assinatura (quem confere
 * é a API e o servidor de jogo); só descarta o que venceu ou não tem cara de token, para não mostrar
 * "conectado" com uma sessão morta.
 */
export const SESSION_KEY = "eldrun:sessao";

export interface StoredSession {
  token: string;
  /** Id da conta (claim `sub`). */
  id: string;
  apelido: string;
  /** Vencimento do JWT, em segundos (claim `exp`). */
  exp: number;
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** `sub` e `exp` do JWT (sem conferir a assinatura), ou `undefined` se não for um JWT legível. */
export function tokenClaims(token: string): { sub: string; exp: number } | undefined {
  const part = token.split(".")[1];
  if (!part || token.split(".").length !== 3) return undefined;
  try {
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "="));
    const { sub, exp } = JSON.parse(json) as { sub?: unknown; exp?: unknown };
    return typeof sub === "string" && sub && typeof exp === "number" && Number.isFinite(exp) ? { sub, exp } : undefined;
  } catch {
    return undefined;
  }
}

/** A sessão guardada, se ainda vale; uma vencida ou estragada é apagada. */
export function readSession(store: Store, now = Date.now()): StoredSession | undefined {
  let raw: string | null;
  try {
    raw = store.getItem(SESSION_KEY);
  } catch {
    return undefined;
  }
  if (!raw) return undefined;
  try {
    const s = JSON.parse(raw) as Partial<StoredSession>;
    if (typeof s.token === "string" && typeof s.id === "string" && typeof s.apelido === "string" && typeof s.exp === "number" && s.exp * 1000 > now) {
      return { token: s.token, id: s.id, apelido: s.apelido, exp: s.exp };
    }
  } catch {
    // estragada: cai no apagar
  }
  clearSession(store);
  return undefined;
}

/** Guarda a sessão de um login ou cadastro; `false` se o token não for legível ou o navegador recusar. */
export function saveSession(store: Store, token: string, apelido: string): boolean {
  const claims = tokenClaims(token);
  if (!claims) return false;
  try {
    store.setItem(SESSION_KEY, JSON.stringify({ token, id: claims.sub, apelido, exp: claims.exp } satisfies StoredSession));
    return true;
  } catch {
    return false;
  }
}

export function clearSession(store: Store): void {
  try {
    store.removeItem(SESSION_KEY);
  } catch {
    // sem armazenamento: nada a apagar
  }
}
